import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, output, input, linkedSignal } from '@angular/core';
import { Validators, FormGroup, FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import {Subject} from 'rxjs';
import {Client} from '@app/modules/shared/models';
import {ActivatedRoute, Router} from '@angular/router';
import {Constants} from '@app/consts';
import {
  IRegistrationPlatformState,
  RegistrationPlatformCreate,
  getRegistrationPlatform,
  ResetRegistrationPlatformState,
  RegistrationPlatformGet,
  RegistrationPlatformUpdate,
  getRegistrationPlatformError,
  RegistrationPlatformGetSuccess,
  RegistrationPlatformValidateSlug,
  getRegistrationPlatformSlug
} from '@app/stores';
import {select, Store} from '@ngrx/store';
import {takeUntil} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { SbInputFormComponent } from '../../../_components/unified/sb-form/sb-form.component';
import { SbInputImageComponent } from '../../../_components/unified/sb-input/sb-input-image.component';
import { InfoPopoverComponent } from '../../../_components/info-popover/info-popover.component';
import { CreatableSelectComponent } from '../../../_components/creatable-select/creatable-select.component';
import { SbTextareaComponent } from '../../../_components/unified/sb-textarea/sb-textarea.component';

interface RegistrationPlatformFormControls {
  name: FormControl<string | null>;
  image: FormControl<string | null>;
  website: FormControl<string | null>;
  settings: FormControl<string | null>;
  widget_integration_guide: FormControl<string | null>;
  api_integration_guide: FormControl<string | null>;
  widget_customization_code: FormControl<string | null>;
  widget_customization_code_old: FormControl<string | null>;
  widget_customization_css: FormControl<string | null>;
  widget_customization_css_old: FormControl<string | null>;
  hide_default_guide: FormControl<number | null>;
  supports_push_links: FormControl<number | null>;
  /** Only while adding: the platform's slug is set once. */
  slug?: FormControl<string | null>;
}

@Component({
  selector: 'app-registration-platform-add',
  templateUrl: './registration-platform-add.component.html',
  styleUrls: ['./registration-platform-add.component.css'],
  providers: [Constants],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PageShellComponent, SbInputFormComponent, ReactiveFormsModule, SbInputImageComponent, InfoPopoverComponent, CreatableSelectComponent, SbTextareaComponent]
})
export class RegistrationPlatformAddComponent implements OnInit, OnDestroy, AfterViewInit {
  private router = inject(Router);
  private formBuilder = inject(FormBuilder);
  constants = inject(Constants);
  private registrationPlatformStore = inject<Store<IRegistrationPlatformState>>(Store);
  private toastrService = inject(ToastrService);
  private readonly cdr = inject(ChangeDetectorRef);
  private toastr = inject(ToastrService);
  private route = inject(ActivatedRoute);

  addRegistrationPlatformForm: FormGroup<RegistrationPlatformFormControls>;
  unsubscriber = new Subject();
  clients: Client[] = [];
  /** Route param on the edit page, or input when opened as a modal. */
  readonly platformIdInput = input<number>(undefined, {alias: 'platformId'});
  readonly platformId = linkedSignal(() => this.platformIdInput());
  /** True when opened inside an NgbModal via EntityFormModalService. */
  readonly isModal = input(false);
  readonly modalDone = output<'saved' | 'cancelled'>();
  submitted = false;
  currentUser: any;
  platform: any = null;
  slugData: string;
  selectedWidgetSupport = null;
  selectedSyncSupport = null;
  widgetSupportValues = [{id: 'basic', name: 'Minimum (Clients might need help and it may not always work)'},
    {id: 'full', name: 'Full (Clients can easily add without any help and works but there are workarounds)'},
    {id: 'ultimate', name: 'Ultimate (Clients can easily add without any help and works reliably)'}];
  syncSupportValues = [{id: 'basic', name: 'Basic (Speakers Only)'},
    {id: 'full', name: 'Full (All Categories)'}];

  constructor() {
    this.registrationPlatformStore.dispatch(ResetRegistrationPlatformState({params: {error: '', registrationPlatform: null, success: ''}}));
    this.subscribeStores();
  }

  subscribeStores() {
    this.registrationPlatformStore.pipe(select(getRegistrationPlatformError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastrService.error(error, 'Error');
        }
      });
    this.registrationPlatformStore.pipe(select(RegistrationPlatformGetSuccess))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(success => {
        if (success) {
          if (this.submitted) {
            this.submitted = false;
          }
        }
      });
    this.registrationPlatformStore.pipe(select(getRegistrationPlatform))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(platform => {
        if (platform) {
          this.platform = Object.assign({}, platform);
          this.addRegistrationPlatformForm.patchValue({
            name: platform.name,
            website: platform.website,
            image: platform.image,
            settings: platform.settings,
            widget_integration_guide: platform.widget_integration_guide || '',
            api_integration_guide: platform.api_integration_guide || '',
            widget_customization_code: platform.widget_customization_code,
            widget_customization_code_old: platform.widget_customization_code_old,
            widget_customization_css: platform.widget_customization_css,
            widget_customization_css_old: platform.widget_customization_css_old,
            hide_default_guide: platform.hide_default_guide === true ? 1 : 0,
            supports_push_links: platform.supports_push_links === true ? 1 : 0,
          });
          this.selectedWidgetSupport = platform.widget_support || null;
          this.selectedSyncSupport = platform.sync_support || null;
          this.cdr.markForCheck();
        }
      });

    this.registrationPlatformStore.pipe(select(getRegistrationPlatformSlug))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(slug => {
        if (slug && slug === 'invalid') {
          this.addRegistrationPlatformForm.controls['slug'].setValue('');
          this.cdr.markForCheck();
          this.toastr.error('Please change the slug, it\'s already taken', 'Error');
        }
      });
  }

  ngOnInit(): void {
    if (this.isModal()) {
      // Opened via EntityFormModalService: platformId comes from the input.
      if (this.platformId()) {
        this.loadRegistrationPlatform(this.platformId());
      }
    } else {
      this.route.paramMap.subscribe(params => {
        if (params.has('id')) {
          this.platformId.set(+params.get('id'));
          this.cdr.markForCheck();
          this.loadRegistrationPlatform(this.platformId());
        }
      });
    }
    this.addRegistrationPlatformForm = this.formBuilder.group({
      name: ['', Validators.required],
      image: [''],
      website: ['', Validators.pattern(this.constants.urlRegex)],
      settings: [''],
      widget_integration_guide: [''],
      api_integration_guide: [''],
      widget_customization_code: [''],
      widget_customization_code_old: [''],
      widget_customization_css: [''],
      widget_customization_css_old: [''],
      hide_default_guide: 0,
      supports_push_links: 0
    }) as FormGroup<RegistrationPlatformFormControls>;
    // Read after the route subscription, which sets the page's id synchronously.
    if (!this.platformId()) {
      this.addRegistrationPlatformForm.addControl('slug',  new FormControl('', Validators.required)); // Add new form control
    }
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  get f() {
    return this.addRegistrationPlatformForm.controls;
  }

  saveRegistrationPlatform() {
    this.submitted = true;
    if (this.addRegistrationPlatformForm.invalid) {
      return;
    }
    this.registrationPlatformStore.dispatch(RegistrationPlatformGetSuccess({registrationPlatform: null}));
    this.saveRegistrationPlatformData();
  }
  setSyncSupport(e) {
    this.selectedSyncSupport = e?.value ? e.value : null;
  }
  setWidgetSupport(e) {
    this.selectedWidgetSupport = e?.value ? e.value : null;
  }

  saveRegistrationPlatformData() {
    // Given the support levels and emptied guides below before it is sent.
    const saveParams: any = this.addRegistrationPlatformForm.value;
    console.log('saveParams', saveParams);

    saveParams['widget_support'] = this.selectedWidgetSupport?.length ? this.selectedWidgetSupport : null;
    saveParams['sync_support'] = this.selectedSyncSupport?.length ? this.selectedSyncSupport : null;

    // An emptied guide is saved as none (the editor holds an empty text as an empty paragraph).
    for (const guide of ['widget_integration_guide', 'api_integration_guide']) {
      if (!saveParams[guide] || saveParams[guide] === '<p></p>') {
        saveParams[guide] = null;
      }
    }
    const platformId = this.platformId();
    if (platformId) {
      this.registrationPlatformStore.dispatch(RegistrationPlatformUpdate({registrationPlatformId: platformId, registrationPlatform: saveParams}));
    } else {
      this.registrationPlatformStore.dispatch(RegistrationPlatformCreate({registrationPlatform: saveParams}));
    }
    if (this.isModal()) {
      this.modalDone.emit('saved');
      return;
    }
    this.cancelClick();
  }

  onChangeCheckbox(event, slug) {
    const isChecked = event.target.checked;
    if (slug === 'hide_default_guide') {
      this.addRegistrationPlatformForm.patchValue({
        hide_default_guide: isChecked ? 1 : 0
      });
    }
    // if (slug === 'widget_support') {
    //   this.addRegistrationPlatformForm.patchValue({
    //     widget_support: isChecked ? 1 : 0
    //   });
    // }
    if (slug === 'supports_push_links') {
      this.addRegistrationPlatformForm.patchValue({
        supports_push_links: isChecked ? 1 : 0
      });
    }
  }

  loadRegistrationPlatform(registrationPlatformId: number) {
    this.registrationPlatformStore.dispatch(RegistrationPlatformGet({registrationPlatformId}));
  }

  cancelClick() {
    if (this.isModal()) {
      this.modalDone.emit('cancelled');
      return;
    }
    this.router.navigate(['settings/registration-platform']);
  }

  generateSlug(): void {
    this.slugData = this.convertToSlug(this.addRegistrationPlatformForm.get('name').value);
    this.addRegistrationPlatformForm.get('slug').setValue(this.slugData);
  }

  convertToSlug(Text) {
    return Text.toLowerCase().replace(/[^\w ]+/g, '').trim().replace(/  +/g, ' ').replace(/ +/g, '-');
  }

  public validateSlug() {
    if (!this.addRegistrationPlatformForm.get('slug').value) {
      return;
    }
    this.registrationPlatformStore.dispatch(RegistrationPlatformValidateSlug({slug: this.addRegistrationPlatformForm.get('slug').value}));
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }
}
